import * as stylex from '@stylexjs/stylex'
import { type StyleXStyles } from '@stylexjs/stylex'
import { cloneElement, type ComponentPropsWithoutRef, type ForwardedRef, type ReactElement } from 'react'

const baseStyles = stylex.create({
  root: {
    fill: 'currentColor',
    display: 'inline-block',
    flexShrink: 0,
    userSelect: 'none',
    height: '1em',
    width: '1em',
  },
  color: (color: string) => ({ color }),
  fontSize: (fontSize: number | string) => ({ fontSize }),
  fill: (fill: string) => ({ fill }),
})

export interface SvgIconProps extends Omit<ComponentPropsWithoutRef<'svg'>, 'color'> {
  /** SVG 元素的 ref */
  rootRef?: ForwardedRef<SVGSVGElement>
  /** SVG 子元素（必須是單一 ReactElement） */
  children: ReactElement
  /** 自訂根元素的 StyleX 樣式 */
  rootStyle?: StyleXStyles
  /**
   * 圖示顏色
   * @default 'inherit'
   */
  color?: string
  /**
   * 圖示大小（字體大小）
   * @default 'inherit'
   */
  fontSize?: number | string
  /** 自訂 SVG fill 屬性 */
  fill?: string
}

/**
 * SvgIcon 是 SVG 圖示的封裝元件，統一處理大小、顏色與 fill。
 * 透過 cloneElement 將樣式注入到子 SVG 元素上。
 */
export const SvgIcon = (props: SvgIconProps) => {
  const { children, rootStyle, color = 'inherit', fontSize = 'inherit', rootRef, fill, ...rest } = props
  return cloneElement(children as ReactElement<{ ref: ForwardedRef<SVGSVGElement> }>, {
    ref: rootRef,
    ...stylex.props(
      baseStyles.root,
      baseStyles.fontSize(fontSize),
      !!color && baseStyles.color(color),
      !!fill && baseStyles.fill(fill),
      rootStyle,
    ),
    ...rest,
  })
}
